import api from './api';

// invited: through the provider's invite link; member: a signed-in CLOUT member
export type ReviewKind = 'invited' | 'member';
export type ReviewStatus = 'pending' | 'approved' | 'rejected';

export interface ReviewInput {
  rating: number;
  review_text: string;
  reviewer_name: string;
  consent: true;
}

export interface PublishedReview {
  id: number;
  rating: number;
  review_text: string;
  reviewer_name: string;
  kind: ReviewKind;
  created_at: string;
}

export interface PublishedReviewList {
  total: number;
  reviews: PublishedReview[];
}

export interface SubmittedReview {
  id: number;
  status: ReviewStatus;
  message: string;
}

export interface InviteLookup {
  provider_id: number;
  provider_name: string;
}

export type InviteResultStatus =
  'sent' | 'already_invited' | 'already_reviewed' | 'own_email' | 'daily_limit' | 'failed';

export interface InviteResult {
  email: string;
  status: InviteResultStatus;
}

export interface SentInvite {
  id: number;
  email: string;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  status: 'pending' | 'used' | 'expired';
}

// Must match RejectionReason in backend/app/schemas/clout_review.py
export const REJECTION_REASONS = {
  health_information: 'Health information',
  personal_information: 'Personal information about someone else',
  abusive: 'Abusive or hateful',
  spam: 'Spam or advertising',
  conflict_of_interest: 'Conflict of interest',
  not_a_client: 'Not a client of this provider',
  off_topic: 'Off topic',
  other: 'Other policy reason',
} as const;

export type RejectionReason = keyof typeof REJECTION_REASONS;

export interface ModeratedReview {
  id: number;
  provider_id: number;
  provider_name: string;
  rating: number;
  review_text: string;
  reviewer_name: string;
  kind: ReviewKind;
  reviewer_email: string | null;
  status: ReviewStatus;
  rejection_reason: RejectionReason | null;
  moderator_note: string | null;
  created_at: string;
  moderated_at: string | null;
}

export interface ModeratedReviewList {
  total: number;
  reviews: ModeratedReview[];
}

export const cloutReviewsApi = {
  // Reviews are switched off until CLOUT_REVIEWS_ENABLED is set on the backend
  getStatus: async (): Promise<{ enabled: boolean }> => {
    const response = await api.get('/api/clout-reviews/status');
    return response.data;
  },

  listPublished: async (
    providerId: number,
    skip: number = 0,
    limit: number = 10
  ): Promise<PublishedReviewList> => {
    const response = await api.get(`/api/providers/${providerId}/reviews`, {
      params: { skip, limit },
    });
    return response.data;
  },

  submitAsMember: async (providerId: number, review: ReviewInput): Promise<SubmittedReview> => {
    const response = await api.post(`/api/providers/${providerId}/reviews`, review);
    return response.data;
  },

  lookUpInvite: async (token: string): Promise<InviteLookup> => {
    const response = await api.post('/api/review-invites/lookup', { token });
    return response.data;
  },

  submitWithInvite: async (token: string, review: ReviewInput): Promise<SubmittedReview> => {
    const response = await api.post('/api/review-invites/submit', { ...review, token });
    return response.data;
  },

  sendInvites: async (emails: string[]): Promise<InviteResult[]> => {
    const response = await api.post('/api/providers/me/review-invites', { emails });
    return response.data;
  },

  listInvites: async (): Promise<SentInvite[]> => {
    const response = await api.get('/api/providers/me/review-invites');
    return response.data;
  },

  listForModeration: async (
    status: ReviewStatus,
    skip: number = 0,
    limit: number = 50
  ): Promise<ModeratedReviewList> => {
    const response = await api.get('/api/admin/clout-reviews', {
      params: { status, skip, limit },
    });
    return response.data;
  },

  approve: async (reviewId: number): Promise<ModeratedReview> => {
    const response = await api.post(`/api/admin/clout-reviews/${reviewId}/approve`);
    return response.data;
  },

  reject: async (
    reviewId: number,
    reason: RejectionReason,
    note?: string
  ): Promise<ModeratedReview> => {
    const response = await api.post(`/api/admin/clout-reviews/${reviewId}/reject`, {
      reason,
      note: note || null,
    });
    return response.data;
  },
};
